import { useEffect, useState } from 'react';
import { apiFetch } from './api';
import { useAuth } from '../contexts/AuthContext';

/* ── API (Phase 25.H — /api/connected-businesses/*) ── */
// Concurrent non-refresh summary calls (Home KPIs + Bakemill card) share ONE request.
const summaryInflight = new Map(); // token -> Promise

export const cbApi = {
  enabled: (t) => apiFetch('/connected-businesses/enabled', { token: t }),
  settings: (t) => apiFetch('/connected-businesses/settings', { token: t }),
  patchSettings: (t, body) => apiFetch('/connected-businesses/settings', { method: 'PATCH', body, token: t }),
  summary: (t, refresh = false) => {
    const call = () => apiFetch(`/connected-businesses/bakemill-summary?refresh=${refresh ? 'true' : 'false'}`, { token: t, timeoutMs: 25000 });
    if (refresh) return call();
    if (!summaryInflight.has(t)) summaryInflight.set(t, call().finally(() => summaryInflight.delete(t)));
    return summaryInflight.get(t);
  },
};

/* ── Session-cached enabled check (one request per login token) ── */
const enabledCache = new Map(); // token -> boolean | Promise<boolean|null>

/** Returns null while loading, then true/false. Cached for the session. */
export function useConnectedBusinessesEnabled() {
  const { token } = useAuth();
  const cachedNow = enabledCache.get(token);
  const [enabled, setEnabled] = useState(typeof cachedNow === 'boolean' ? cachedNow : null);

  useEffect(() => {
    if (!token) { setEnabled(null); return undefined; }
    const c = enabledCache.get(token);
    if (typeof c === 'boolean') { setEnabled(c); return undefined; }
    let alive = true;
    const p = c || cbApi.enabled(token).then(r => r?.enabled === true).catch(() => null);
    enabledCache.set(token, p);
    p.then(v => {
      if (v === null) enabledCache.delete(token); // transient error → retry on next mount
      else enabledCache.set(token, v);
      if (alive) setEnabled(v === true);
    });
    return () => { alive = false; };
  }, [token]);

  return enabled;
}

/* ── Last successful sync (per tenant, per browser session) ── */
const lastGoodKey = (tenantId) => `cb_bakemill_last_good:${tenantId || 'unknown'}`;

export function readLastGood(tenantId) {
  try { return JSON.parse(sessionStorage.getItem(lastGoodKey(tenantId))) || null; } catch { return null; }
}

export function writeLastGood(tenantId, res) {
  try {
    sessionStorage.setItem(lastGoodKey(tenantId), JSON.stringify({ data: res.data, last_synced_at: res.last_synced_at }));
  } catch { /* storage full / disabled — non-fatal */ }
}

/* ── Error classification ── */
export function cbErrorInfo(e) {
  const d = e?.detail && typeof e.detail === 'object' ? e.detail : null;
  const detail = (d && (d.detail || d.message)) || e?.message || 'Unknown error';
  const unreachable = e?.status === 0 || /network|timeout|timed out/i.test(String(detail));
  return { detail: String(detail), unreachable, status: e?.status ?? 0 };
}

/**
 * The preview/prod edge (Cloudflare) replaces origin 502 bodies with an HTML
 * page, so `{code:"integration_fetch_failed", detail}` may never reach the
 * browser. The backend records the real error on the config row *before*
 * raising, so recover it from GET /settings (owner/manager only).
 */
export async function resolveSyncError(token, e) {
  const info = cbErrorInfo(e);
  if (e?.status !== 502 || e?.detail?.code) return info;
  try {
    const c = await cbApi.settings(token);
    if (c?.last_sync_status === 'error' && c.last_sync_error) {
      return { ...info, detail: c.last_sync_error, unreachable: /network|timeout/i.test(c.last_sync_error) };
    }
  } catch { /* non-owner/manager → 403; fall through */ }
  return { ...info, detail: 'Integration server returned an error (502)', unreachable: false };
}

/* ── Payload normalizer ──
 * Accepts both the documented shape ({erp:{...}, showrooms, manufacturing})
 * and the live upstream shape (flat ERP keys, {count, items} objects,
 * retail_showrooms with franchise_revenue_split). Missing → null ("—"). */
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const itemsOf = (v) => (Array.isArray(v) ? v : Array.isArray(v?.items) ? v.items : []);
const countOf = (v) => {
  if (Array.isArray(v)) return v.length;
  if (isObj(v)) return num(v.count) ?? (Array.isArray(v.items) ? v.items.length : null);
  return num(v);
};

function normShowroom(s) {
  const fs = s.franchise_split || s.franchise_revenue_split || {};
  const notApplicable = fs.applicable === false;
  return {
    name: s.name || s.code || '—',
    city: s.city || null,
    todaySales: num(s.today_sales),
    monthlyRevenue: num(s.monthly_revenue),
    stockAlerts: countOf(s.stock_alerts),
    ownerSplit: notApplicable ? null : num(fs.owner_amount ?? fs.month?.owner_share),
    franchiseeSplit: notApplicable ? null : num(fs.franchisee_amount ?? fs.month?.franchisee_share),
    ownerPct: num(fs.owner_pct),
    franchiseePct: num(fs.franchisee_pct),
  };
}

export function normalizeSummary(data) {
  const d = isObj(data) ? data : {};
  const erp = isObj(d.erp) ? d.erp : d;
  const m = isObj(d.manufacturing) ? d.manufacturing : null;
  const showrooms = Array.isArray(d.showrooms) ? d.showrooms : Array.isArray(d.retail_showrooms) ? d.retail_showrooms : null;
  const produced = m?.production_completed_today;
  return {
    generatedAt: d.generated_at || null,
    erp: {
      todaySales: num(erp.today_sales_total ?? erp.today_total_sales),
      monthlyRevenue: num(erp.monthly_revenue),
      pendingPOs: countOf(erp.pending_purchase_orders),
      lowStockCount: countOf(erp.low_stock_alerts),
      lowStock: itemsOf(erp.low_stock_alerts).filter(isObj).map(i => ({
        sku: i.sku_code ?? i.code ?? i.sku ?? null, name: i.name ?? null,
        qty: num(i.qty ?? i.quantity), threshold: num(i.threshold ?? i.reorder_level),
      })),
      topProducts: itemsOf(erp.top_selling_products).filter(isObj).map(i => ({
        name: i.name ?? null, qty: num(i.qty_sold ?? i.quantity), revenue: num(i.revenue),
      })),
    },
    showrooms: showrooms ? showrooms.filter(isObj).map(normShowroom) : null,
    mfg: m && {
      active: num(m.active_production_orders),
      producedToday: isObj(produced) ? num(produced.quantity) : num(produced),
      producedBatches: isObj(produced) ? num(produced.batches) : null,
      rawAlertsCount: countOf(m.raw_material_stock_alerts),
      rawAlerts: itemsOf(m.raw_material_stock_alerts).filter(isObj).map(i => ({ name: i.name ?? null, qty: num(i.qty ?? i.quantity) })),
      pendingDispatch: countOf(m.pending_dispatch_orders),
    },
  };
}

export const fmtCount = (n) => (n == null ? '—' : Number(n).toLocaleString());
